import classNames from "classnames";
import InfiniteProgress from "components/_common/InfiniteProgress";
import React, { useEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import {
	ECNR_BASE_RANGE_TYPE,
	ICNRCellInfo,
	ICNRRangesVesselsLoadParams,
	ICNRRangeVesselLocalRange,
} from "redux/app/report/@types";
import reportActions from "redux/app/report/actions";
import reportSelectors from "redux/app/report/selectors";
import { ESIGNAL_REPORT_TYPE_VAL } from "redux/app/vessel/onboard/types";
import { formatSRTypeValues } from "redux/app/vessel/onboard/utils";
import { IStore } from "redux/store";
import debounce from "lodash/debounce";

type OwnProps = {
	toggleEditModalVisible: (isEditModalVisible?: boolean) => void;
	setRangeInfo: (rangeInfo?: ICNRRangeVesselLocalRange) => void;
	reportType?: string;
	setCellInfo: React.Dispatch<React.SetStateAction<ICNRCellInfo>>;
	setVesselIdOfCellToBeUpdated: React.Dispatch<React.SetStateAction<string>>;
	baseRangeType: ECNR_BASE_RANGE_TYPE;
	isFileUploading?: boolean;
};
type CNRRangeTableProps = PropsFromRedux & OwnProps;
function CNRRangeTable(props: CNRRangeTableProps) {
	const {
		cnrRangeVessels,
		columns,
		pagination,
		toggleEditModalVisible,
		setRangeInfo,
		reportType,
		setCellInfo,
		setVesselIdOfCellToBeUpdated,
		baseRangeType,
		vesselsSelected,
		isFileUploading = false,
		jobToLoadCNRRangesVessels,
	} = props;
	const { skip, hasMore } = pagination;
	const { isLoading, isError } = jobToLoadCNRRangesVessels;

	// --- Refs for Infinite Scroll ---
	const observer = useRef<IntersectionObserver | null>(null);
	const tableRef = useRef<HTMLTableElement | null>(null);
	const loaderRef = useRef<HTMLDivElement | null>(null);
	const firstLoadRef = useRef(true);
	const [showHorizontalScroll, setShowHorizontalScroll] = useState(false);
	const [showVerticalScroll, setShowVerticalScroll] = useState(false);

	const containerRef = useRef<HTMLDivElement>(null);

	const selectedVessels = vesselsSelected?.join(",");

	useEffect(() => {
		firstLoadRef.current = true;
	}, [selectedVessels]);

	useEffect(() => {
		const currentLoaderRef = loaderRef.current;
		if (isLoading || !hasMore || !currentLoaderRef) return;

		const scrollContainer = tableRef.current?.parentElement;

		const option = {
			root: scrollContainer,
			rootMargin: "5px", // Load a bit before it's fully visible
			threshold: 0.1,
		};

		const debouncedLoad = debounce(() => {
			props.loadCNRRangeVessels({
				baseRangeType,
				selectedVessels: vesselsSelected,
				paginationParams: {
					...pagination,
				},
			});
		}, 100);

		const loadHandler = () => {
			if (firstLoadRef.current) {
				// For first load, call immediately
				props.loadCNRRangeVessels({
					baseRangeType,
					selectedVessels: vesselsSelected,
					paginationParams: {
						...pagination,
					},
				});
				firstLoadRef.current = false;
			} else {
				// For subsequent loads, using debounced version
				debouncedLoad();
			}
		};

		observer.current = new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting && hasMore) {
				loadHandler();
			}
		}, option);

		if (currentLoaderRef) {
			observer.current.observe(currentLoaderRef);
		}

		return () => {
			if (observer.current && currentLoaderRef) {
				observer.current.unobserve(currentLoaderRef);
			}
			if (observer.current) {
				observer.current.disconnect();
			}
			debouncedLoad.cancel();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, hasMore, skip, baseRangeType, selectedVessels]);

	useEffect(() => {
		const handleScroll = () => {
			if (!containerRef.current) return;

			const container = containerRef.current;
			const {
				scrollLeft,
				scrollTop,
				scrollWidth,
				scrollHeight,
				clientWidth,
				clientHeight,
			} = container;

			// Show/hide horizontal scroll arrow based on horizontal scroll position
			const maxHorizontalScroll = scrollWidth - clientWidth;
			setShowHorizontalScroll(scrollLeft > 0 && maxHorizontalScroll > 0);

			// Show/hide vertical scroll arrow based on vertical scroll position
			const maxVerticalScroll = scrollHeight - clientHeight;
			setShowVerticalScroll(scrollTop > 0 && maxVerticalScroll > 0);
		};

		const container = containerRef.current;
		if (container) {
			container.addEventListener("scroll", handleScroll);
			return () => container.removeEventListener("scroll", handleScroll);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (isError) {
		return (
			<div className="w-full justify-center items-center">
				Failed to fetch the data. Please try again later.
			</div>
		);
	}

	// These below if case - To handle loading situation that wasn't possible within from div. Because of table(related div) size decreasing based on number of columns

	if (
		!isLoading &&
		!columns?.length &&
		!cnrRangeVessels?.length &&
		!pagination.hasMore
	) {
		return (
			<div className="text-center my-12">
				There is no data available for {reportType?.toUpperCase()}
			</div>
		);
	}

	return (
		<div className="bg-gray-50 w-full relative">
			<div
				ref={containerRef}
				className={classNames("max-h-[65vh]", {
					"overflow-x-auto shadow rounded border-gray-200 ws-scroll w-full cnr-ranges-table":
						columns.length,
					"lg:w-fit": columns?.length < 5 && columns?.length !== 0,
				})}
			>
				{(columns?.length === 0 || cnrRangeVessels?.length === 0) &&
				!isLoading &&
				!pagination.hasMore ? (
					<div className="text-center my-12">
						There is no data available for {reportType?.toUpperCase()}
					</div>
				) : (
					<InfiniteProgress
						isLoading={isLoading || isFileUploading}
						isSpacedOut={false}
					/>
				)}

				<table
					ref={tableRef}
					className={classNames(
						"min-w-full divide-y divide-gray-300 bg-white max-w-fit",
						{ hidden: !columns.length || !cnrRangeVessels?.length }
					)}
				>
					<thead className="sticky top-0 z-30 bg-gray-100">
						<tr className="sticky top-0">
							<th
								scope="col"
								className="sticky whitespace-nowrap -left-[1.5px] z-10  w-48 sm:w-64 max-w-[256px]"
							>
								<div
									className={classNames(
										"px-4 py-2.5 text-left text-[12px] lg:text-sm font-medium text-gray-700 bg-gray-100 border-r-2 border-gray-300"
									)}
								>
									Vessel Name
								</div>
							</th>

							{columns.map((col, colIndex) => {
								let titleParts = col.title?.split(" > ");
								titleParts = titleParts.filter((part) => !part?.includes("_"));
								// titleParts?.reverse()
								const displayableTitle = titleParts?.join(" > ");

								const title = displayableTitle ?? col.title;
								// const title = titleStr?.trim() ?? col.title;

								return (
									<th
										key={col.key}
										scope="col"
										className={classNames(
											"px-4 py-2.5 text-left text-[12px] lg:text-sm font-medium text-gray-700 whitespace-nowrap hover:bg-gray-200 transition-colors duration-150 border-r-[1.5px]",
											{
												"border-b-[1.5px] lg:min-w-[200px]":
													colIndex === cnrRangeVessels?.length - 1,
											}
										)}
										title={col.title}
									>
										<div className="truncate max-w-xs">{title}</div>
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-200 bg-white">
						{cnrRangeVessels.map((vessel, index) => (
							<tr
								key={vessel.vesselId || `vessel-${index}`}
								className="hover:bg-gray-50 transition-colors duration-150"
							>
								{/* Fixed Vessel Name Cell */}
								<td className="sticky -left-[1.5px] z-0 whitespace-nowrap w-48 sm:w-64 lg:min-w-[200px] max-w-[256px] border-b-[1.5px] border-gray-300">
									<div
										className={classNames(
											"px-1 py-2 text-[12px] lg:text-sm  font-medium text-gray-800 bg-white border-r-2 border-gray-300 flex items-center"
										)}
									>
										<span className="w-[18px] h-[18px] flex items-center justify-center text-xs bg-gray-500 text-gray-200  font-medium rounded-full border-gray-600 p-[6px] mr-1 text-left">
											{(index + 1).toString()?.padStart(2, "0")}
										</span>
										<span>{vessel.vesselName}</span>
									</div>
								</td>
								{/* Dynamic Metric Cells */}
								{columns.map((col, colIndex) => {
									const ranges =
										vessel.localRanges && vessel.localRanges[col.key];
									const rangeValue = vessel.localRanges[col.key]?.range;

									return (
										<td
											key={col.key}
											className={classNames(
												"whitespace-nowrap px-4 py-2 text-[12px] lg:text-sm text-gray-600 text-center border-r max-w-fit  border-b-[1.5px] border-gray-300",
												{
													"bg-orange-100":
														ranges?.rangeType !== "ERROR" && rangeValue?.length,
													"bg-red-100": ranges?.rangeType === "ERROR",
												}
											)}
											onClick={() => {
												toggleEditModalVisible(true);
												setCellInfo({
													key: col.key,
													metric: col.title,
													type: reportType ?? "",
												});
												setVesselIdOfCellToBeUpdated(vessel.vesselId);
												setRangeInfo(ranges);
											}}
										>
											<div className="flex items-center justify-center">
												<span>
													{vessel.localRanges && vessel.localRanges[col.key]
														? JSON.stringify(rangeValue)
														: ""}
												</span>
												<span
													className={classNames(
														"bp3-icon text-[12px] lg:text-sm font-medium ml-1 inline-block mt-0.5",
														{
															"bp3-icon-error text-red-500":
																ranges?.rangeType === "ERROR",
															"bp3-icon-warning-sign text-orange-400":
																ranges?.rangeType !== "ERROR" && rangeValue,
														}
													)}
													title={
														rangeValue
															? ranges?.rangeType === "ERROR"
																? "ERROR"
																: "WARNING"
															: undefined
													}
												/>
											</div>
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
				<div
					ref={loaderRef} // If any issues make h-10 mt-4
					className={classNames(
						"h-0.5 flex justify-center items-center mt-0 w-full sticky left-0",
						{
							"h-10 mt-4 mb-2": isLoading,
						}
					)}
				>
					{isLoading && <p className="text-gray-600">Loading vessels...</p>}
				</div>
			</div>
			{/* scroll arrows */}

			{!(isLoading || isFileUploading) ? (
				<div
					className={classNames("absolute inline-block", {
						"bottom-5 right-5": cnrRangeVessels?.length >= 2,
						"bottom-1.5 right-2.5": cnrRangeVessels?.length === 1,
					})}
				>
					<div className="flex justify-center  space-x-2">
						{columns?.length > 0 && showHorizontalScroll ? (
							<button
								title="Scroll to start"
								onClick={() => {
									const tableContainer =
										document.querySelector(".cnr-ranges-table");
									if (tableContainer) {
										tableContainer.scrollTo({
											left: 0,
											behavior: "smooth",
										});
									}
								}}
								className="flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors duration-200 border border-gray-300"
							>
								<span className="bp3-icon bp3-icon-chevron-left" />
							</button>
						) : null}
						{cnrRangeVessels?.length > 0 && showVerticalScroll ? (
							<button
								onClick={() => {
									const tableContainer =
										document.querySelector(".cnr-ranges-table");
									if (tableContainer) {
										tableContainer.scrollTo({
											top: 0,
											behavior: "smooth",
										});
									}
								}}
								className="flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors duration-200 border border-gray-300"
								title="Scroll to Top"
							>
								<span className="bp3-icon bp3-icon-chevron-up" />
							</button>
						) : null}
					</div>
				</div>
			) : null}
		</div>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const reportStore = reportSelectors._getStore(store);
	const pagination = reportSelectors.getCNRRangesPagination(reportStore);
	const cnrRangeVessels =
		reportSelectors.getCNRRangesRawVesselList(reportStore);
	const reportType =
		reportSelectors.getCNRRangesReportTypeSelected(reportStore);
	// This is being done to ensure steaming is passed instead of streaming.
	const formattedReportType = formatSRTypeValues(
		reportType as ESIGNAL_REPORT_TYPE_VAL
	);

	let columns = reportSelectors.getCNRRangesColumns(reportStore);

	columns = columns.filter((col) => {
		// steaming has this errorneous value which we can't disturb now.
		return col?.type?.toLowerCase() === formattedReportType?.toLowerCase();
	});

	const jobToLoadCNRRangesVessels =
		reportSelectors.jobCNRRangesVesselsLoad(reportStore);

	const vesselsSelected =
		reportSelectors.getCNRRangesVesselsSelected(reportStore);
	return {
		cnrRangeVessels,
		pagination,
		columns,
		vesselsSelected,
		jobToLoadCNRRangesVessels,
	};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {
		loadCNRRangeVessels(params: ICNRRangesVesselsLoadParams) {
			dispatch(reportActions.commands.cnrRangesVesselsLoad({ ...params }));
		},
	};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(CNRRangeTable);
